import { useState } from "react";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import Button from "../ui/button/Button";
import Label from "../form/Label";
import { CopyIcon, ErrorIcon } from "../../icons";
import Alert from "../ui/alert/Alert";
import { request } from "../../utils";

interface apiKeyInt {
  apiKey: string
}

export default function ApiKey() {
  const [apiKey, setApiKey] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  // ---- Modal states
  const {
    isOpen: isKeyModalOpen,
    openModal: openKeyModal,
    closeModal: closeKeyModal,
  } = useModal();

  // ---- Generate new API key
  const generateNewKey = async () => {
    try {
      setIsGenerating(true);
      setError("");

      const response = await request.post<apiKeyInt>({
        url: "/user/api-key",
      });

      setApiKey(response?.data?.apiKey as string);
      openKeyModal();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    if (!apiKey) return;

    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-6 lg:flex-row lg:justify-between">
          <div className="w-full text-center lg:text-left lg:w-auto">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              API Key
            </h4>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Generate an API key to authenticate with our API
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex-1 p-3 rounded-lg border border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 min-h-[42px] flex items-center">
              {isGenerating ? (
                <span className="text-gray-500 dark:text-gray-400">
                  Generating...
                </span>
              ) : apiKey ? (
                <span className="font-mono text-gray-800 dark:text-white/90">
                  ••••••••••••••••••••••••
                </span>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">
                  ••••••••••••••••••••••••
                </span>
              )}
            </div>

            <button
              onClick={generateNewKey}
              disabled={isGenerating}
              className="min-w-[120px] px-4 py-4 text-sm rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:bg-brand-400 disabled:cursor-not-allowed relativeflex items-center justify-center
              "
            >
              {isGenerating ? (
                <>
                  Generating...
                </>
              ) : (
                "Generate Key"
              )}
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {
              "Your key is securely stored. Click the button to generate a new API key."
            }
          </p>
        </div>
      </div>

      {/* API Key Display Modal */}
      <Modal
        isOpen={isKeyModalOpen}
        onClose={closeKeyModal}
        className="w-full max-w-2xl"
      >
        <div className="relative w-full p-6 overflow-y-auto bg-white no-scrollbar rounded-3xl dark:bg-gray-900">
          <div className="mb-6">
            <h4 className="mb-1 text-2xl font-semibold text-gray-800 dark:text-white/90">
              Secure API Key
            </h4>
            <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <ErrorIcon /> This API key can only be viewed once. Store it
              securely.
            </p>
          </div>

          {error && (
            <div className="mb-6">
              <Alert variant="error" title="Error" message={error} />
            </div>
          )}

          <div className="mb-6">
            <Label>Your New API Key</Label>
            <div className="relative">
              <div className="flex items-center gap-2 p-3 rounded-lg border border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                <span className="font-mono text-gray-800 dark:text-white/90 break-all flex-1 overflow-x-auto pr-8">
                  {apiKey}
                </span>
                <button
                  onClick={copyToClipboard}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <span className="text-sm text-brand-500">Copied!</span>
                  ) : (
                    <CopyIcon className="fill-current size-5" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button size="sm" onClick={closeKeyModal}>
              I've Saved My Key
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
